import React, { useMemo, useState } from 'react';

import { Alert, Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useUnit } from 'effector-react';

import { attachClient, detachClient } from '../../../../api';
import { ProtectedComponent } from '../../../../components/ProtectedComponent';
import { ConfirmationDialog } from '../../../../components/UIKit/ConfirmationDialog';
import { clientDetailsApi } from '../../../../effector/clientDetails';
import { Strategies$ } from '../../../../effector/strategies';
import { StatusEnum } from '../../../../types/clientDetails';
import { Strategy } from '../../../../types/strategy';
import { UserAction } from '../../../../utils/permissions';
import { Nullable } from '../../../../utils/types';

export const CLIENT_STATUS_AF = 'Автоследование';
export const CLIENT_STATUS_AC = 'Автоконсультирование';
export const CLIENT_STATUS_IB = 'Инвест-копилка';
export const CLIENT_STATUS_NONE = 'Не подключен';

type StrategyOption = { label: string; value: string };
type TypeOption = { label: string; value: StatusEnum };

const allStrategyTypes: TypeOption[] = [
  { label: CLIENT_STATUS_AF, value: StatusEnum.AutoFollow },
  { label: CLIENT_STATUS_AC, value: StatusEnum.Recommendations },
  { label: CLIENT_STATUS_IB, value: StatusEnum.InvestMoneybox },
  { label: 'Отсутствует', value: StatusEnum.Unattached },
];

const getFollowTypes = (strategy?: Strategy): TypeOption[] => {
  if (!strategy) return [];

  return allStrategyTypes.filter(
    (t) =>
      (t.value === StatusEnum.AutoFollow && strategy.isAutofollow) ||
      (t.value === StatusEnum.Recommendations && strategy.isAutoconsult) ||
      (t.value === StatusEnum.InvestMoneybox && strategy.isInvestbox),
  );
};

export const AttachDetach: React.FC<{ id: string; strategyId: Nullable<string>; type: StatusEnum }> = ({
  id,
  strategyId,
  type,
}) => {
  const strategiesStore = useUnit(Strategies$);

  const initialStrategy = useMemo(
    () => strategiesStore?.find((s) => s.id === strategyId),
    [strategyId, strategiesStore],
  );

  const initialFollowTypes = useMemo(() => getFollowTypes(initialStrategy) || [], [initialStrategy]);

  const [selectedStrategy, setSelectedStrategy] = useState<StrategyOption | null>(
    initialStrategy ? { value: initialStrategy.id, label: initialStrategy.name } : null,
  );
  const [followTypes, setFollowTypes] = useState<TypeOption[]>(initialFollowTypes);
  const [selectedType, setSelectedType] = useState<TypeOption | null>(
    initialFollowTypes.find((t) => t.value === type) ?? null,
  );
  const [send, setSend] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);
  const [showDetachDialog, setShowDetachDialog] = useState(false);

  const strategyOptions: StrategyOption[] = useMemo(
    () => strategiesStore?.map((s) => ({ label: s.name, value: s.id })) ?? [],
    [strategiesStore],
  );

  const handleStrategyChange = (_: unknown, value: StrategyOption | null) => {
    setSelectedStrategy(value);

    if (value) {
      const strategy = strategiesStore?.find((s) => s.id === value.value);
      const types = getFollowTypes(strategy);
      const typeValue = types.length > 0 ? types[0] : null;

      setFollowTypes(types);
      setSelectedType(typeValue);
    } else {
      setFollowTypes([]);
      setSelectedType(null);
    }
  };

  const handleTypeChange = (_: unknown, value: TypeOption | null) => {
    setSelectedType(value);
  };

  const handleAttach = () => {
    if (!selectedStrategy || !selectedType || selectedType.value === StatusEnum.Unattached) return;

    setSend(true);
    attachClient({ agreementId: id, strategy: selectedStrategy.value, status: selectedType.value })
      .then((result) => {
        if (result && result.success) {
          clientDetailsApi.updateClientInfo({
            strategyId: selectedStrategy.value,
            strategyName: strategiesStore?.find((s) => s.id === selectedStrategy.value)?.name ?? 'Unknown',
            status: selectedType.value,
          });
          setError(null);
        } else {
          setError(result?.message ?? 'На сервере произошла ошибка');
        }
      })
      .catch((e) => setError(e.toString()))
      .finally(() => setSend(false));
  };

  const handleDetach = () => {
    setSend(true);
    detachClient(id)
      .then((result) => {
        if (result && result.success) {
          setError(null);
        } else {
          setError(result?.errorMessage ?? 'На сервере произошла ошибка');
        }
      })
      .catch((e) => setError(e.toString()))
      .finally(() => {
        setSend(false);
        setShowDetachDialog(false);
      });
  };

  const isAttachDisabled = send || !selectedStrategy || !selectedType || selectedType.value === StatusEnum.Unattached;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Подключение данного аккаунта
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Autocomplete
          sx={{ minWidth: 250 }}
          size="small"
          disabled={send}
          options={strategyOptions}
          value={selectedStrategy}
          onChange={handleStrategyChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          renderInput={(params) => <TextField {...params} label="Стратегия" placeholder="Стратегия" />}
        />
        <Autocomplete
          sx={{ minWidth: 220 }}
          size="small"
          disabled={send}
          options={followTypes}
          value={selectedType}
          onChange={handleTypeChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          renderInput={(params) => <TextField {...params} label="Тип следования" placeholder="Тип следования" />}
        />
        <Button variant="contained" disabled={isAttachDisabled} onClick={handleAttach}>
          Подключить
        </Button>
      </Stack>

      <ProtectedComponent userAction={UserAction.statisticClientDetailsDetach}>
        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
          Отключение данного аккаунта
        </Typography>
        <Button variant="contained" color="error" disabled={send} onClick={() => setShowDetachDialog(true)}>
          Отключить
        </Button>
      </ProtectedComponent>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {showDetachDialog && (
        <ConfirmationDialog
          title="Отключение клиента от стратегии"
          description="Вы уверены что хотите отключить аккаунт клиента от стратегии?"
          isLoading={send}
          onConfirm={handleDetach}
          onClose={() => setShowDetachDialog(false)}
        />
      )}
    </Box>
  );
};
